import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PrintableColor } from '@customedge/shared';
import { nearestPantone, PmsReadout } from './PantoneMatch';

// Shared viewport-clamping math for any color-picker popover anchored to a
// trigger element — extracted from VariantSelector.tsx's ColorPickerSwatch
// (the original fix for this bug class: a floating 3D-preview swatch whose
// native `<input type="color">` opened clipped/hidden behind an
// `overflow:hidden` ancestor). Opens upward when there's room, else below,
// and always clamps both axes into the viewport so the popover can never
// render partly off-screen — the exact failure now reported for the left
// "Select color" swatch grid (hidden behind the canvas) and the right
// text/stroke color inputs (clipped at the window's right edge).
export function usePopoverPosition(open: boolean, popoverWidth: number, popoverHeight: number) {
  const triggerRef = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // const opensUp = rect.top - popoverHeight > 8;
    // setPos({
    //   top: opensUp ? rect.top - popoverHeight - 8 : Math.min(rect.bottom + 8, window.innerHeight - popoverHeight - 8),
    //   left: Math.min(Math.max(rect.left, 8), window.innerWidth - popoverWidth - 8),
    // });
    const availableAbove = rect.top - 8;
const availableBelow = window.innerHeight - rect.bottom - 8;
const opensUp =
  availableAbove >= popoverHeight ||
  availableAbove > availableBelow;

setPos({
  top: Math.max(
    8,
    Math.min(
      opensUp ? rect.top - popoverHeight - 8 : rect.bottom + 8,
      window.innerHeight - popoverHeight - 8
    )
  ),
  left: Math.max(
    8,
    Math.min(rect.left, window.innerWidth - popoverWidth - 8)
  ),
});

const update = () => {
  const r = triggerRef.current?.getBoundingClientRect();
  if (!r) return;

  const above = r.top - 8;
  const below = window.innerHeight - r.bottom - 8;
  const up = above >= popoverHeight || above > below;

  setPos({
    top: Math.max(
      8,
      Math.min(
        up ? r.top - popoverHeight - 8 : r.bottom + 8,
        window.innerHeight - popoverHeight - 8
      )
    ),
    left: Math.max(
      8,
      Math.min(r.left, window.innerWidth - popoverWidth - 8)
    ),
  });
};

window.addEventListener('resize', update);
window.addEventListener('scroll', update, true);

return () => {
  window.removeEventListener('resize', update);
  window.removeEventListener('scroll', update, true);
};
  }, [open, popoverWidth, popoverHeight]);

  return { triggerRef, pos };
}

// Portals its children onto document.body with position:fixed viewport
// coordinates, so no ancestor's overflow:hidden/position:absolute panel can
// clip or mis-anchor it. Closes on outside click or Escape.
export function PortaledPopover({ top, left, width, onClose, children }: {
  top: number;
  left: number;
  width: number;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top,
        left,
        width,
        zIndex: 2000,
        background: "var(--surface, white)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 12,
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxHeight: "calc(100vh - 16px)",
        overflowY: "auto",
        overflowX: 'hidden',
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

interface ColorPickerProps {
  value:    string;
  onChange: (color: string) => void;
  label?:   string;
  // Print-color-count budget enforcement (useColorBudget) — both optional so
  // every existing caller keeps working unrestricted. When supplied,
  // `handleChange` blocks committing a color that isn't already in use once
  // the budget is exhausted, rather than silently letting the pick through.
  isColorAllowed?: (hex: string) => boolean;
  budgetLabel?:    string;
  usedColors?:     string[];
  // True once every slot the active print-color count allows is already
  // occupied — per direct user request ("once user reach the max color it
  // give only design colors as the option to select text, stroke color"):
  // at this point picking a genuinely NEW color is never going to succeed
  // (isColorAllowed would just block it and jump to the offending object
  // instead), so the free hex wheel/text input are hidden entirely rather
  // than shown-but-guaranteed-to-fail — the customer can ONLY choose among
  // the design's own already-used colors. `usedColors` must be non-empty for
  // this to actually change anything (an at-limit budget with zero used
  // colors can't happen — a limit of 0 has no slots to fill in the first
  // place — but the guard costs nothing).
  isAtLimit?: boolean;
  // Fires once per blocked attempt (not on every keystroke of an already-
  // blocked state) — lets a caller "jump to the object using up the limit"
  // (see TextTool.tsx's onBlocked wiring to findOffendingObject/
  // selectOffendingObject) instead of leaving the customer to just read the
  // inline block message and hunt for the culprit themselves.
  onBlocked?: () => void;
}

async function fetchColors(): Promise<PrintableColor[]> {
  const r = await fetch('/api/printable-colors');
  const d = await r.json();
  return (d.colors ?? []) as PrintableColor[];
}

const POPOVER_WIDTH = 240;
const POPOVER_HEIGHT = 420;
// The at-limit popover drops the free hex wheel entirely (see isAtLimit's
// own comment) — just the "your colors" swatch row, the read-only PMS
// readout, and a close button, so it needs far less vertical room than the
// full picker.
const AT_LIMIT_POPOVER_HEIGHT = 140;

export function ColorPicker({ value, onChange, label, isColorAllowed, budgetLabel, usedColors = [], isAtLimit = false, onBlocked }: ColorPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen]   = useState(false);
  const [hex,  setHex]    = useState(value);
  const [blockedMessage, setBlockedMessage] = useState(false);
  // Once every slot is taken, there is nothing left to browse for — a
  // free-pick popover would just present a wheel/CMYK grid where every
  // non-matching pick is guaranteed to bounce off isColorAllowed. Swatch-only
  // mode needs less height, so the popover's own reserved space shrinks too
  // (keeps it snug rather than leaving a large empty gap below a 3-swatch row).
  const swatchOnly = isAtLimit && usedColors.length > 0;
  const { triggerRef, pos } = usePopoverPosition(open, POPOVER_WIDTH, swatchOnly ? AT_LIMIT_POPOVER_HEIGHT : POPOVER_HEIGHT);

  // Sync local state when the parent updates the value prop (e.g. selecting a different object)
  useEffect(() => { setHex(value); }, [value]);
  const { data: colors = [] } = useQuery({ queryKey: ['printable-colors'], queryFn: fetchColors, staleTime: 300_000, enabled: !swatchOnly });

  const handleChange = (color: string) => {
    if (isColorAllowed && !isColorAllowed(color)) {
      setBlockedMessage(true);
      onBlocked?.();
      return;
    }
    setBlockedMessage(false);
    setHex(color);
    onChange(color);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 78 }}>{label ?? t('colorPicker.defaultLabel')}</label>
        <button ref={triggerRef as React.RefObject<HTMLButtonElement>} onClick={() => setOpen(!open)} style={{
          width: 27, height: 27, borderRadius: 5, border: '2px solid var(--border)',
          background: hex, cursor: 'pointer', flexShrink: 0,
        }} />
        {/* Free-text hex entry is just as much an escape hatch around the
            budget as the wheel is — hidden together with it once at the
            limit, per direct user request ("give only design colors as the
            option"), not merely disabled-but-visible. */}
        {!swatchOnly && (
          <input value={hex} onChange={e => handleChange(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }} />
        )}
      </div>

      {budgetLabel && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{budgetLabel}</p>
      )}
      {blockedMessage && (
        <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
          {t('colorPicker.limitReached')}
        </p>
      )}

      {open && pos && (
        <PortaledPopover top={pos.top} left={pos.left} width={POPOVER_WIDTH} onClose={() => setOpen(false)}>
          {usedColors.length > 0 && (
            <div style={swatchOnly ? undefined : { marginBottom: 8 }}>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t('colorPicker.yourColors')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {usedColors.map(c => (
                  <button key={c} onClick={() => { handleChange(c); setOpen(false); }}
                    style={{ width: 20, height: 20, background: c, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 3, cursor: 'pointer' }}
                    title={c} />
                ))}
              </div>
            </div>
          )}

          {!swatchOnly && (
            <HexColorPicker color={hex} onChange={handleChange} />
          )}

          {/* Shows only the SINGLE nearest PMS match for whatever hex is
              currently picked (nearestPantone), never a browse-all-colors
              grid — with the admin's printable-colors library realistically
              sized at 1000+ real Pantone entries (see PrintableColors.tsx),
              rendering every one as its own clickable swatch was reported
              live as completely unusable clutter (screenshot: rows and rows
              of tiny swatches filling most of the popover). Outside the
              swatchOnly branch, deliberately — informational only, reads
              whatever hex is already picked rather than letting the
              customer introduce a new one, so it must keep showing even
              once the design's own color budget is exhausted and the free
              hex wheel above is hidden (same reasoning as
              BackgroundColorTool.tsx's SwatchEditorPopover — see its own
              comment on this same pattern). */}
          {colors.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <PmsReadout match={nearestPantone(hex, colors)} />
            </div>
          )}

          <button onClick={() => setOpen(false)} style={{ marginTop: 8, width: '100%', padding: 4, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>{t('colorPicker.close')}</button>
        </PortaledPopover>
      )}
    </div>
  );
}
